import type { ScaleLinear, ScaleSymLog, ScaleTime } from 'd3-scale';

import { isIsolatedPoint } from '../../../utils/is-isolated-point.js';
import { sanitizeZeroValues } from '../../../utils/sanitize-zero-values.js';
import {
  TIMESTAMP_BAR_WIDTH,
  TIMESTAMP_BAR_X_DEVIATION,
} from '../constants.js';
import type { SparklineDatapointMetadata } from '../types/sparkline-props.js';

/**
 * Get x-accessor base on xScale
 *
 * @param xScale - The x-axis scale
 * @returns The sparkline chart x-accessor
 */
export const xAxisAccessor =
  (xScale: ScaleTime<number, number>) =>
  (dataPoint: SparklineDatapointMetadata) =>
    xScale(dataPoint.end ?? dataPoint.start) ?? 0;

/**
 * Get y-accessor base on yScale
 *
 * @param yScale - The y-axis scale
 * @returns The sparkline chart y0-accessor
 */
export const yAccessor =
  (yScale: ScaleLinear<number, number> | ScaleSymLog<number, number>) =>
  (dataPoint: SparklineDatapointMetadata) =>
    yScale(dataPoint.value) ?? 0;

/**
 * Get y0-accessor base on yScale
 *
 * @param yScale - The y-axis scale
 * @returns The sparkline chart y0-accessor
 */
export const y0Accessor =
  (yScale: ScaleLinear<number, number> | ScaleSymLog<number, number>) => () =>
    sanitizeZeroValues(yScale, 0) ?? 0;

/**
 * Get y1-accessor base on yScale
 *
 * @param yScale - The y-axis scale
 * @returns The sparkline chart y1-accessor
 */
export const y1Accessor =
  (yScale: ScaleLinear<number, number> | ScaleSymLog<number, number>) =>
  (dataPoint: SparklineDatapointMetadata) =>
    yScale(dataPoint.value) ?? 0;

/**
 * Get if data-point is a gap
 *
 * @param datapoint - The data points
 * @returns Whether the accessor is defined
 */
export const definedAccessor = (
  datapoint: SparklineDatapointMetadata,
): boolean => {
  return !datapoint.isGap;
};

/**
 * Get accessor if data point has gap before and after
 *
 * @param dataPoints - The data points
 * @param index - The index of the data point in the data points array
 */
export const isIsolatedAccessor = (
  dataPoints: SparklineDatapointMetadata[],
  index: number,
) => isIsolatedPoint(dataPoints, index, (point) => definedAccessor(point));

/**
 * Get x-accessor for bar variant of the sparkline chart
 *
 * @param xScale - The x-axis scale
 * @returns The x-accessor for bar variant of the sparkline chart
 */
export const barXAxisAccessor =
  (xScale: ScaleTime<number, number>) =>
  (dataPoint: SparklineDatapointMetadata): number => {
    const { start, end } = dataPoint;
    const isTimeframe = end && end !== start;
    const deviation = isTimeframe ? 0 : TIMESTAMP_BAR_X_DEVIATION;

    return (xScale(start) ?? 0) - deviation;
  };

/**
 * Get y-accessor for bar variant of the sparkline chart
 *
 *
 * @param yScale - The y-axis scale
 * @returns The y-accessor for bar variant of the sparkline chart
 */
export const barYAxisAccessor =
  (yScale: ScaleLinear<number, number> | ScaleSymLog<number, number>) =>
  (dataPoint: SparklineDatapointMetadata): number => {
    const maxValue = yScale.invert(0);
    const maxValueCoordinate = sanitizeZeroValues(yScale, maxValue);
    const cumulativeCoordinate = sanitizeZeroValues(yScale, dataPoint.value);

    return maxValueCoordinate + cumulativeCoordinate;
  };

/**
 * Get a bar width for the sparkline chart
 *
 * @param datapoint - Sparkline's data point
 * @param xScale - The x-axis scale
 * @returns The bar width
 */
export const barWidthAccessor = (
  datapoint: SparklineDatapointMetadata,
  xScale: ScaleTime<number, number>,
): number => {
  const { start, end } = datapoint;
  const isTimeframe = end && end !== start;

  return isTimeframe
    ? (xScale(end) ?? 0) - (xScale(start) ?? 0)
    : TIMESTAMP_BAR_WIDTH;
};

/**
 * Get a bar height for the sparkline chart
 *
 * @param datapoint - Sparkline's data point
 * @param yScale - The y-axis scale
 * @returns The bar height
 */
export const barHeightAccessor = (
  datapoint: SparklineDatapointMetadata,
  yScale: ScaleLinear<number, number> | ScaleSymLog<number, number>,
): number => {
  const valueCoordinate = sanitizeZeroValues(yScale, datapoint.value);
  const zeroCoordinate = sanitizeZeroValues(yScale, 0);

  return zeroCoordinate - valueCoordinate;
};
